<template>
  <section
    ref="sectionEl"
    class="relative py-24 bg-white dark:bg-dark overflow-hidden transition-colors duration-300"
    :class="{ 'is-visible': isVisible }"
  >
    <div class="section-glow-top"></div>
    <div class="section-glow-bottom"></div>

    <div class="container mx-auto px-4 relative z-10">
      <div class="max-w-3xl mx-auto text-center mb-14 anim-el d1">
        <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">
          Dati di mercato
        </p>
        <h2 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.1] mb-6 transition-colors duration-300">
          I clienti sono già online.
        </h2>
        <p class="text-lg text-gray-500 dark:text-gray-400 leading-relaxed">
          Trasforma la presenza digitale in vendite con una strategia concreta, misurabile e pronta per crescere.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto mb-16">
        <article
          v-for="(item, index) in marketStats"
          :key="index"
          class="rounded-2xl bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/5 hover:border-blue-200 dark:hover:border-blue-500/25 group anim-el"
          :class="'d' + (index + 2)"
        >
          <div class="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110">
            <component :is="item.icon" class="h-5 w-5 text-blue-500" />
          </div>
          <p class="text-2xl font-bold text-gray-900 dark:text-white mb-1">{{ item.value }}</p>
          <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{{ item.title }}</p>
          <p class="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">{{ item.description }}</p>
        </article>
      </div>

      <div class="max-w-6xl mx-auto anim-el d7">
        <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4 text-center">
          Checklist operativa
        </p>
        <h3 class="text-2xl md:text-3xl text-center font-bold text-gray-900 dark:text-white mb-8">
          Verifica in pochi secondi se sei pronto a vendere online
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <button
            v-for="(list, index) in checklists"
            :key="list.title"
            type="button"
            class="rounded-xl border p-4 text-left transition-all duration-300"
            :class="activeChecklist === index
              ? 'bg-blue-50 border-blue-200 dark:bg-blue-500/10 dark:border-blue-400/30 shadow-sm'
              : 'bg-white border-gray-200 hover:border-gray-300 dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:border-white/20'"
            @click="activeChecklist = index"
          >
            <div class="flex items-center gap-2.5 mb-2">
              <div class="w-8 h-8 rounded-lg bg-gray-900/5 dark:bg-white/10 flex items-center justify-center">
                <component :is="list.icon" class="h-4 w-4 text-gray-700 dark:text-gray-200" />
              </div>
              <span class="text-sm font-semibold text-gray-900 dark:text-white">{{ list.shortTitle }}</span>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400">{{ list.helper }}</p>
          </button>
        </div>

        <Transition name="fade-slide" mode="out-in">
          <article
            :key="checklists[activeChecklist].title"
            class="rounded-2xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] p-6 md:p-7"
          >
            <div class="flex items-center gap-3 mb-5">
              <div class="w-10 h-10 rounded-xl bg-gray-900/5 dark:bg-white/10 flex items-center justify-center">
                <component :is="checklists[activeChecklist].icon" class="h-5 w-5 text-gray-700 dark:text-gray-200" />
              </div>
              <h4 class="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                {{ checklists[activeChecklist].title }}
              </h4>
            </div>

            <ul class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <li
                v-for="(point, pointIndex) in checklists[activeChecklist].items"
                :key="pointIndex"
                class="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed rounded-lg bg-gray-50 dark:bg-white/[0.03] p-3.5"
              >
                <CheckCircle2 class="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>{{ point }}</span>
              </li>
            </ul>
          </article>
        </Transition>
      </div>

      <div class="text-center mt-12 anim-el d8">
        <p class="text-gray-500 dark:text-gray-400 mb-5">
          Ti aiutiamo a trasformare questi punti in un piano operativo in 30 giorni.
        </p>
        <router-link
          to="/contact"
          class="inline-flex items-center h-12 px-7 bg-gray-900 dark:bg-white text-white dark:text-dark font-semibold text-[15px] rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors group"
        >
          Richiedi una consulenza
          <ArrowRight class="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </router-link>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import {
  ArrowRight,
  CheckCircle2,
  Search,
  Eye,
  ShoppingCart,
  Globe,
  Store,
  MessageSquareText,
  Users
} from 'lucide-vue-next'

const sectionEl = ref(null)
const isVisible = ref(false)
const activeChecklist = ref(0)
let observer = null

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) isVisible.value = true
    })
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
  if (sectionEl.value) observer.observe(sectionEl.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const marketStats = [
  {
    icon: Search,
    value: '80%',
    title: 'Ricerca online prima della visita',
    description: 'La maggior parte dei clienti controlla online prima di recarsi in sede.'
  },
  {
    icon: ShoppingCart,
    value: '40%',
    title: 'Preferisce ordinare online',
    description: 'Una quota crescente compra online invece di andare direttamente in negozio.'
  },
  {
    icon: Users,
    value: '4.4M',
    title: 'Svizzeri attivi sui social',
    description: 'Solo il 20% delle PMI e presente con un profilo strutturato.'
  },
  {
    icon: Globe,
    value: '77%',
    title: 'Ordina online ogni mese',
    description: 'Gli acquisti digitali fanno parte delle abitudini mensili in Svizzera.'
  },
  {
    icon: MessageSquareText,
    value: '1 su 3',
    title: 'Legge recensioni prima di scegliere',
    description: 'La reputazione online influenza direttamente la decisione finale.'
  }
]

const checklists = [
  {
    icon: Search,
    shortTitle: 'Essere trovato',
    helper: 'Posizionamento e visibilita locale',
    title: 'Lista di controllo - come essere trovato?',
    items: [
      'La tua attivita e presente su tutte le piattaforme digitali rilevanti?',
      'Ottieni clienti da Google, Maps, Bing, Facebook e Instagram?',
      'Compari nei sistemi di navigazione usati dai clienti?',
      'Il sito e ottimizzato per smartphone?'
    ]
  },
  {
    icon: Eye,
    shortTitle: 'Essere visto',
    helper: 'Immagine, fiducia e aggiornamenti',
    title: 'Lista di controllo - come essere visto?',
    items: [
      'Il sito e il tuo miglior biglietto da visita?',
      'I clienti capiscono subito cosa offri e perche sceglierti?',
      'Prezzi, orari e informazioni sono aggiornati su tutte le piattaforme?',
      'Sito e annunci rispettano standard moderni di sicurezza e visibilita?'
    ]
  },
  {
    icon: Store,
    shortTitle: 'E-commerce',
    helper: 'Vendere anche quando il negozio e chiuso',
    title: 'Lista di controllo - e-commerce',
    items: [
      'I clienti sanno come ordinare online da voi in pochi passaggi?',
      'Avete prodotti spedibili che possono ampliare il fatturato online?',
      'In caso di eventi critici, potete continuare a vendere anche da remoto?',
      'Lo shop supporta i metodi di pagamento piu usati e standard di sicurezza aggiornati?'
    ]
  }
]
</script>

<style scoped>
.section-glow-top {
  position: absolute;
  top: 8%;
  left: 50%;
  transform: translateX(-50%);
  width: 55%;
  height: 36%;
  background: radial-gradient(ellipse at center, rgba(59, 130, 246, 0.08) 0%, transparent 62%);
  pointer-events: none;
}

.section-glow-bottom {
  position: absolute;
  bottom: -8%;
  right: -10%;
  width: 34%;
  height: 45%;
  background: radial-gradient(ellipse at center, rgba(59, 130, 246, 0.06) 0%, transparent 62%);
  pointer-events: none;
}

.anim-el {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}

.is-visible .anim-el {
  opacity: 1;
  transform: translateY(0);
}

.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.11s; }
.d3 { transition-delay: 0.17s; }
.d4 { transition-delay: 0.23s; }
.d5 { transition-delay: 0.29s; }
.d6 { transition-delay: 0.35s; }
.d7 { transition-delay: 0.42s; }
.d8 { transition-delay: 0.5s; }

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
